import type { HostCapabilities } from "../../capabilities";
import type { MarlinStoreDb } from "../infra/db";
import type { IpJsonContent, LegacyConfig } from "../types";
import * as adoption from "./adoption";
import * as settings from "../settings/store";
import { warn } from "../infra/effects";

export type AdoptionFromHost = {
  config: adoption.AdoptionResult | null;
  ipJson: boolean;
  ipJsonError?: string;
};
const hasSites = (content: IpJsonContent | null | undefined): boolean =>
  Array.isArray(content?.SITES) && content.SITES.length > 0;

export const createLegacyHost = (
  db: MarlinStoreDb,
  capabilities: Pick<HostCapabilities, "files">,
) => {
  const { files } = capabilities;
  const adoptFromHost = async (): Promise<AdoptionFromHost> => {
    if (!files) return { config: null, ipJson: false };
    const config = (await files
      .readLegacyConfig()
      .catch(() => null)) as LegacyConfig | null;
    let configResult: adoption.AdoptionResult | null = null;
    if (config) {
      try {
        configResult = await adoption.adoptLegacyConfig(db, config);
      } catch (error) {
        warn("legacy config adoption")(error);
        configResult = await adoption.markLegacyConfigFailed(db, error);
      }
    }
    if (configResult?.reason === "failed")
      return { config: configResult, ipJson: false };
    const legacyIpJson = (await files
      .readLegacyIpJson()
      .catch(() => null)) as IpJsonContent | null;
    let imported = false;
    let ipJsonError: string | undefined;
    try {
      imported = await adoption.importLegacyIpJson(db, legacyIpJson);
    } catch (error) {
      warn("legacy ip.json import")(error);
      ipJsonError = await adoption.markLegacyIpJsonFailed(db, error);
    }
    const content = await settings.getIpJsonContent(db);
    if (content && (imported || (hasSites(content) && !hasSites(legacyIpJson))))
      await files.writeIpJson(content).catch(warn("ip.json write"));
    return { config: configResult, ipJson: imported, ipJsonError };
  };
  return { adoptFromHost };
};
